import * as React from "react";

import { X } from "lucide-react";

type PillTextAreaProps = {
  pills: string[];
  onChange: (newPills: string[]) => void;
};

const PillTextArea: React.FC<PillTextAreaProps> = ({ pills, onChange }) => {
  const [inputValue, setInputValue] = React.useState("");
  const input = React.useRef<HTMLInputElement>(null);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Note (Sebas,2023-11-14): This is needed to prevent the input value from being updated when
    // the user pastes a value. This is because the paste is handled in another function.
    // @ts-expect-error
    if (event.nativeEvent.inputType === "insertFromPaste") {
      return;
    }
    setInputValue(event.target.value);
  };
  const showPlaceholder = pills.length === 0 && inputValue.length === 0;

  const handleAddPill = (value: string) => {
    const trimmedValue = value.trim();
    if (trimmedValue !== "") {
      onChange([...pills, trimmedValue]);
      setInputValue("");
      input.current!.value = "";
    }
  };

  const handleInputKeydown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      (event.key === "Backspace" || event.key === "Delete") &&
      inputValue === ""
    ) {
      const newPills = [...pills];
      newPills.pop();
      onChange(newPills);
    } else if (
      event.key === "Enter" ||
      event.key === "," ||
      event.key === " "
    ) {
      event.preventDefault();
      handleAddPill(inputValue);
    }
  };

  const handleOnBlur = () => {
    if (inputValue !== "") {
      handleAddPill(inputValue);
    }
  };

  const handleRemovePill = (indexToRemove: number) => {
    onChange(pills.filter((_p, pillIndex) => pillIndex !== indexToRemove));
  };

  const handleInputPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const clipboardData = event.clipboardData;
    const pastedText = clipboardData.getData("text");

    const values = pastedText
      .split(",")
      .map((value) => value.trim())
      .filter((value) => value !== "");

    onChange([...pills, ...values]);
    setInputValue("");
  };

  return (
    <div className="flex w-full flex-col gap-1">
      <div
        className="bg-sheet focus-within:ring-primary border-0.5 border-component-border relative flex min-h-[150px] w-full cursor-text flex-wrap content-start gap-1 rounded-sm p-3 text-sm focus-within:ring-1 focus-within:ring-inset"
        onClick={() => {
          // Note (Sebas,2023-10-02): This is needed to make the cursor go to the end of the input when
          // the user clicks on the div. This is because the input is hidden and the div is the one
          // that receives the click.
          const currentInputValue = input.current?.value;
          input.current!.value = "";
          setTimeout(() => {
            input.current!.value = currentInputValue ?? "";
          }, 0);
          input.current?.focus();
        }}
      >
        {showPlaceholder && (
          <span className="text-muted absolute pt-1 text-sm">
            email@example.com, email2@example.com
          </span>
        )}
        {pills.map((pill, index) => (
          <div
            key={index}
            className="text-default bg-surface flex h-fit items-center gap-1 rounded-sm py-1 pl-2 pr-1 text-sm"
          >
            {pill}
            <X
              size={18}
              onClick={() => handleRemovePill(index)}
              className="cursor-pointer"
            />
          </div>
        ))}
        <input
          ref={input}
          onClick={(event) => event.stopPropagation()}
          autoFocus
          type="text"
          className="outline-hidden h-7 w-min overflow-hidden bg-transparent text-sm focus-visible:ring-0"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleOnBlur}
          onKeyDown={handleInputKeydown}
          size={input.current?.value.length || 1}
          onPaste={handleInputPaste}
          data-1p-ignore
          data-bwignore
          data-lpignore="true"
          data-form-type="other"
        />
      </div>
    </div>
  );
};

export default PillTextArea;
